import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

async function errorMessageFromResponse(response, fallback) {
  try {
    const data = await response.json();
    if (data && typeof data.message === 'string') return data.message;
  } catch {
    /* ignore */
  }
  return fallback;
}

function formatUserName(user) {
  const parts = [user.first_name, user.last_name, user.surname].filter(Boolean);
  return parts.join(' ');
}

function roleLabel(type) {
  if (type === 'admin') return 'Администратор';
  if (type === 'client') return 'Клиент';
  return type || '—';
}

function quotaTextClass(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) return 'text-muted';
  if (value < 0) return 'text-danger fw-semibold';
  return '';
}

function quotaValue(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return String(value);
}

function OfficeDetailView() {
  const { officeId } = useParams();
  const { authFetch } = useAuth();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [amount, setAmount] = useState('1');
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const loadDetail = useCallback(
    async (isRefetch = false) => {
      setError(null);
      if (!isRefetch) setLoading(true);
      try {
        const response = await authFetch(`/api/v1/offices/${officeId}`);
        if (response.ok) {
          const data = await response.json();
          setDetail(data);
        } else if (response.status === 403) {
          const msg = await errorMessageFromResponse(response, 'Нет доступа к этому офису.');
          if (isRefetch) setFormError(msg);
          else setError(msg);
          if (!isRefetch) setDetail(null);
        } else if (response.status === 404) {
          const msg = await errorMessageFromResponse(response, 'Офис не найден.');
          if (isRefetch) setFormError(msg);
          else setError(msg);
          if (!isRefetch) setDetail(null);
        } else {
          const msg = await errorMessageFromResponse(response, 'Не удалось загрузить офис.');
          if (isRefetch) setFormError(msg);
          else setError(msg);
          if (!isRefetch) setDetail(null);
        }
      } catch (e) {
        if (isRefetch) setFormError('Ошибка при обновлении: ' + e.message);
        else {
          setError('Ошибка при загрузке офиса: ' + e.message);
          setDetail(null);
        }
      } finally {
        if (!isRefetch) setLoading(false);
      }
    },
    [authFetch, officeId]
  );

  useEffect(() => {
    loadDetail(false);
  }, [officeId, loadDetail]);

  const handleAddPackage = async (e) => {
    e.preventDefault();
    setFormError(null);
    const n = parseInt(amount, 10);
    if (Number.isNaN(n) || n < 1) {
      setFormError('Укажите количество не менее 1.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await authFetch(`/api/v1/offices/${officeId}/signed-doc-packages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: n }),
      });
      if (response.status === 201) {
        setAmount('1');
        await loadDetail(true);
      } else if (response.status === 400) {
        setFormError(await errorMessageFromResponse(response, 'Некорректные данные.'));
      } else if (response.status === 403) {
        setFormError(await errorMessageFromResponse(response, 'Нет доступа.'));
      } else if (response.status === 404) {
        setFormError(await errorMessageFromResponse(response, 'Офис не найден.'));
      } else {
        setFormError(await errorMessageFromResponse(response, 'Не удалось добавить пакет.'));
      }
    } catch (err) {
      setFormError('Ошибка сети: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Загрузка...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error && !detail) {
    return (
      <div className="container mt-4">
        <Link to="/offices" className="btn btn-outline-secondary btn-sm mb-3">
          ← К списку офисов
        </Link>
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  if (!detail) {
    return null;
  }

  const users = Array.isArray(detail.users) ? detail.users : [];
  const packages = Array.isArray(detail.packages) ? detail.packages : [];

  return (
    <div className="container mt-4">
      <Link to="/offices" className="btn btn-outline-secondary btn-sm mb-3">
        ← К списку офисов
      </Link>
      <h2 className="mb-4">{detail.name}</h2>

      <section className="mb-5">
        <h4 className="h5 mb-3">Квота подписей</h4>
        <div className="card">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-sm-6 col-lg-3">
                <div className="text-muted small">Использовано</div>
                <div className="fs-5">{quotaValue(detail.quota_used)}</div>
              </div>
              <div className="col-sm-6 col-lg-3">
                <div className="text-muted small">Осталось</div>
                <div className={`fs-5 ${quotaTextClass(detail.quota_remaining)}`}>{quotaValue(detail.quota_remaining)}</div>
              </div>
            </div>
            <div className="text-muted small mt-2">
              «Осталось» может быть отрицательным, если квота превышена.
            </div>
          </div>
        </div>
      </section>

      <section className="mb-5">
        <h4 className="h5 mb-3">Пользователи офиса</h4>
        <div className="card">
          <div className="card-body p-0">
            {users.length === 0 ? (
              <p className="p-3 text-muted mb-0">Нет пользователей.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-striped mb-0">
                  <thead>
                    <tr>
                      <th>Имя</th>
                      <th>Email</th>
                      <th>Роль</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td>{formatUserName(user)}</td>
                        <td>{user.email}</td>
                        <td>{roleLabel(user.type)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="mb-5">
        <h4 className="h5 mb-3">Купленные пакеты подписей</h4>
        <div className="card">
          <div className="card-body p-0">
            {packages.length === 0 ? (
              <p className="p-3 text-muted mb-0">Пока нет записей о покупках.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-striped mb-0">
                  <thead>
                    <tr>
                      <th>Количество</th>
                      <th>Дата</th>
                    </tr>
                  </thead>
                  <tbody>
                    {packages.map((pkg) => (
                      <tr key={pkg.id}>
                        <td>{pkg.amount}</td>
                        <td>{new Date(pkg.created_at).toLocaleString('ru-RU')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="mb-4">
        <h4 className="h5 mb-3">Добавить пакет</h4>
        <div className="card">
          <div className="card-body">
            <form onSubmit={handleAddPackage} className="row g-3 align-items-end">
              <div className="col-auto">
                <label htmlFor="package-amount" className="form-label">
                  Количество подписей в пакете
                </label>
                <input
                  id="package-amount"
                  type="number"
                  className="form-control"
                  min={1}
                  step={1}
                  value={amount}
                  onChange={(ev) => setAmount(ev.target.value)}
                  disabled={submitting}
                />
              </div>
              <div className="col-auto">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                      Сохранение…
                    </>
                  ) : (
                    'Добавить'
                  )}
                </button>
              </div>
            </form>
            {formError && (
              <div className="alert alert-danger mt-3 mb-0" role="alert">
                {formError}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default OfficeDetailView;
